import { useAppStore } from "@/store/utils/hooks";
import { FC } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { Session } from "@/components/active-session/Session";
import { Uuid } from "@/domains/global/identifiers";
import { MdsCard, MdsCardKind, MdsCardRadius, MdsCardSize } from "@/design-system/components/card";
import { MDSCardSlotName } from "@/mds/components/card/card.types";
import { ZIndex } from "@/domains/design/constants";

interface ActiveSessionProps {}

export const ActiveSessions: FC<ActiveSessionProps> = observer(() => {
  const { store } = useAppStore();

  const unfinishedSessions = store.sessions.getSessionsExceptByStatus("DISMISSED");
  const importSessions = unfinishedSessions.filter(
    session => session.kind === "IMPORT" || session.kind === "AUTO_ORGANIZE"
  );

  // if there are no processing import sessions, don't render this component
  if (importSessions.length === 0) return null;

  const handleDismissSession = (sessionId: Uuid) => {
    store.sessions.markSessionAsDismissed(sessionId);
  };

  return (
    <StyledSessionContainer>
      <StyledCard
        kind={MdsCardKind.LargeShadow}
        size={MdsCardSize.Auto}
        radius={MdsCardRadius.Large}
        dataTestId="active-sessions-card"
      >
        <div slot={MDSCardSlotName.Content}>
          {importSessions.map(session => {
            const events = store.sessionEvents.getSessionEventsBySessionId(session.id);
            const status = session.status ?? "PROCESSING";

            return (
              <Session
                key={session.id}
                title={session.title ?? "Untitled"}
                sessionId={session.id}
                status={status}
                events={events}
                onDismiss={() => handleDismissSession(session.id)}
              />
            );
          })}
        </div>
      </StyledCard>
    </StyledSessionContainer>
  );
});

const StyledSessionContainer = styled.div(({ theme }) => ({
  position: "absolute",
  right: theme.spacing.md,
  bottom: theme.spacing.md,
  zIndex: ZIndex.FloatingTooltip,
}));

const StyledCard = styled(MdsCard)(({ theme }) => ({
  padding: theme.spacing.lg,
  backgroundColor: theme.colors.white,
  width: 400,
}));
